import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import SearchableSelect from '../shared/SearchableSelect';
import SearchableMultiSelect from '../shared/SearchableMultiSelect';
import ScheduleFields, { scheduleBadgeText } from '../shared/ScheduleFields';
import { ToggleSwitch } from '../shared/FormFields';

// The Workspace Agents page body: assignment RULES. One rule pins a source
// workflow (account → workspace → workflow) to a destination (account or All
// accounts → workspace or All workspaces); '*' destinations auto-cover
// workspaces and accounts created later. Each rule has its own schedule and
// can be run on demand — an all-scoped rule optionally for particular
// store(s). Legacy `new_workspace_workflow` tags render as read-only rows so
// they stay visible and removable.
//
// A pair whose workflow needs Google/Meta ads but whose account isn't
// integrated (or whose workspace hasn't picked an ads account) shows "Needs
// setup" and can't be run or enabled; on all-scoped rules the same guard
// skips those workspaces per run and reports them instead.

const PAGE_SIZE = 25;
// Mirrors the server's RULE_RUN_MAX_WORKSPACES.
const RUN_MAX_WORKSPACES = 100;
const ALL = '*';
const LEGACY_TAG = 'new_workspace_workflow';

const s = {
  note: { fontSize: '12px', lineHeight: 1.6, color: 'var(--text-secondary)', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px', margin: 0 },
  sectionTitle: { fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', margin: 0 },
  assignGrid: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  assignCol: { flex: '1 1 170px', minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '5px' },
  assignLabel: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' },
  assignFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' },
  hint: { fontSize: '12px', color: 'var(--text-muted)' },
  primaryBtn: (disabled) => ({
    padding: '8px 18px', borderRadius: '8px', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap',
    border: '1px solid var(--primary)', background: disabled ? '#e2e8f0' : 'var(--primary)',
    color: disabled ? 'var(--text-muted)' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  divider: { border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0 0' },
  filters: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' },
  search: { flex: '1 1 220px', minWidth: '180px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', outline: 'none' },
  filterSelect: { padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', minWidth: '170px' },
  card: { border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '10px 14px', background: '#fbfcfe', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)', verticalAlign: 'middle' },
  name: { fontWeight: 600 },
  meta: { fontSize: '11px', color: 'var(--text-muted)' },
  empty: { fontSize: '13px', color: 'var(--text-muted)', padding: '18px 14px' },
  pill: (bg, color, border) => ({ display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: '99px', background: bg, color, border: `1px solid ${border}`, fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }),
  badges: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' },
  actions: { display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' },
  actionBtn: (disabled, danger) => ({
    padding: '5px 10px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap',
    border: `1px solid ${danger ? '#fecaca' : 'var(--border)'}`, background: '#fff',
    color: disabled ? 'var(--text-muted)' : danger ? '#dc2626' : 'var(--text-primary)',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
  }),
  editorCell: { padding: '14px', background: '#fbfcfe', borderBottom: '1px solid var(--border)' },
  editorRow: { display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', justifyContent: 'space-between' },
  editorLeft: { display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' },
  missingList: { margin: '8px 0 0', paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6, color: '#b91c1c' },
  reportList: { margin: '4px 0 0', paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6, color: '#b45309' },
  failList: { margin: '4px 0 0', paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6, color: '#b91c1c' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', fontSize: '12px', color: 'var(--text-muted)' },
  pageBtn: (disabled) => ({ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: '#fff', color: disabled ? 'var(--text-muted)' : 'var(--text-primary)', fontWeight: 600, fontSize: '12px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }),
  dialogOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 },
  dialog: { background: '#fff', borderRadius: '12px', border: '1px solid var(--border)', padding: '18px 20px', width: 'min(440px, 92vw)', display: 'flex', flexDirection: 'column', gap: '12px' },
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// Neutral for anything unknown — run status vocab differs between engines.
const statusPill = (status) => {
  const v = String(status || '').toLowerCase();
  if (!v) return null;
  if (v.includes('fail') || v.includes('error')) return s.pill('#fef2f2', '#dc2626', '#fecaca');
  if (v === 'partial') return s.pill('#fffbeb', '#b45309', '#fde68a');
  if (['queued', 'admitted', 'running', 'pending'].some((w) => v.includes(w))) return s.pill('#fffbeb', '#b45309', '#fde68a');
  return s.pill('#ecfdf5', '#047857', '#a7f3d0');
};

const missingMessages = (row) => (row.missing || []).map((m) => m.message);

export default function WorkspaceAssignmentsPanel() {
  // —— pickers ——
  const [stores, setStores] = useState([]);
  const [wsRows, setWsRows] = useState([]);
  const [wsTruncated, setWsTruncated] = useState(false);
  const [src, setSrc] = useState({ shop: '', workspaceId: '', workflowId: '' });
  const [srcFlows, setSrcFlows] = useState([]);
  const [dst, setDst] = useState({ shop: '', workspaceId: '' });
  const [assigning, setAssigning] = useState(false);

  // —— table ——
  const [rows, setRows] = useState([]);
  const [legacyRows, setLegacyRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState('');

  // —— per-row schedule editor + busy actions + run report ——
  const [expandedId, setExpandedId] = useState('');
  const [draft, setDraft] = useState(null); // { schedule, enabled, dirty, saving }
  const [busyId, setBusyId] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [runReport, setRunReport] = useState([]); // amber per-pair skip/fail lines
  const [runDialog, setRunDialog] = useState(null); // { rule, shops: [] }

  useEffect(() => {
    api.get('/admin/stores').then(({ data }) => setStores(data)).catch(() => toast.error('Failed to load stores'));
    api.get('/admin/workspaces')
      .then(({ data }) => {
        setWsRows(data.rows || []);
        setWsTruncated(data.truncated === true);
      })
      .catch(() => toast.error('Failed to load workspaces'));
  }, []);

  // Cascading source picker: account (+ workspace) narrows the workflow list.
  useEffect(() => {
    setSrcFlows([]);
    setSrc((p) => ({ ...p, workflowId: '' }));
    if (!src.shop) return;
    api.get('/admin/workflows', {
      params: { shop: src.shop, ...(src.workspaceId ? { workspaceId: src.workspaceId } : {}) },
    })
      .then(({ data }) => setSrcFlows(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load that store’s workflows'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src.shop, src.workspaceId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setSkip(0); }, [debouncedSearch, shopFilter, workspaceFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/workspace-agents', {
        params: {
          limit: PAGE_SIZE,
          skip,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(shopFilter ? { shop: shopFilter } : {}),
          ...(workspaceFilter ? { workspaceId: workspaceFilter } : {}),
        },
      });
      setRows(data.rows || []);
      setLegacyRows(data.legacyRows || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load assignments');
      setRows([]);
      setLegacyRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [skip, debouncedSearch, shopFilter, workspaceFilter]);

  useEffect(() => { load(); }, [load]);

  // New rules sort first (createdAt desc) — jump back to page one to see them.
  const reload = useCallback(() => {
    if (skip !== 0) setSkip(0);
    else load();
  }, [skip, load]);

  const storeOptions = useMemo(
    () => [...stores].sort((a, b) => a.name.localeCompare(b.name)).map((st) => ({ value: st.shopKey, label: st.name, hint: st.shopKey })),
    [stores],
  );
  const srcWsOptions = useMemo(() => (
    src.shop
      ? [{ value: 'account', label: 'Account level' },
        ...wsRows.filter((w) => w.accountShop === src.shop).map((w) => ({ value: w.id, label: w.name || w.id }))]
      : []
  ), [wsRows, src.shop]);
  const srcFlowOptions = useMemo(
    () => srcFlows.map((wf) => ({ value: wf._id, label: wf.name })),
    [srcFlows],
  );
  const dstShopOptions = useMemo(
    () => [{ value: ALL, label: 'All accounts' }, ...storeOptions],
    [storeOptions],
  );
  const dstWsOptions = useMemo(() => (
    dst.shop && dst.shop !== ALL
      ? [{ value: ALL, label: 'All workspaces' },
        ...wsRows.filter((w) => w.accountShop === dst.shop).map((w) => ({ value: w.id, label: w.name || w.id }))]
      : [{ value: ALL, label: 'All workspaces' }]
  ), [wsRows, dst.shop]);
  const wsFilterOptions = useMemo(
    () => (shopFilter ? wsRows.filter((w) => w.accountShop === shopFilter) : wsRows),
    [wsRows, shopFilter],
  );

  const assignReady = Boolean(src.workflowId && dst.shop && dst.workspaceId);

  const assign = async () => {
    if (!assignReady || assigning) return;
    setAssigning(true);
    const tid = toast.loading('Assigning…');
    try {
      const { data } = await api.post('/admin/workspace-agents', {
        workflowId: src.workflowId,
        targetShop: dst.shop,
        targetWorkspaceId: dst.workspaceId,
      });
      toast.success(data.existing ? 'Already assigned — showing the existing row' : 'Assigned', { id: tid });
      const warnings = missingMessages(data.rule || {});
      if (warnings.length) toast(`Can't run yet: ${warnings.join(' ')}`, { icon: '⚠️', duration: 6000 });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to assign', { id: tid });
    } finally {
      setAssigning(false);
    }
  };

  const withRowBusy = async (row, action, fn) => {
    if (busyId) return;
    setBusyId(row.id);
    setBusyAction(action);
    try {
      await fn();
    } finally {
      setBusyId('');
      setBusyAction('');
    }
  };

  const spliceRow = (updated) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  const describeRunOutcome = (rule, data) => {
    const parts = [`${data.queued} run${data.queued === 1 ? '' : 's'} queued`];
    if (data.skipped) parts.push(`${data.skipped} skipped`);
    if (data.failed) parts.push(`${data.failed} failed`);
    toast[data.failed ? 'error' : 'success'](`${rule.templateName}: ${parts.join(', ')} — runs bill the target accounts`);
    const wsName = (id) => wsRows.find((w) => w.id === id)?.name || id;
    setRunReport((data.results || [])
      .filter((r) => r.skipped === 'ads_requirements' || r.error)
      .map((r) => `${wsName(r.workspaceId)}: ${r.error || (r.missing || []).map((m) => m.message).join(' ') || 'skipped'}`));
  };

  const runRule = (row, shops) => withRowBusy(row, 'run', async () => {
    setRunReport([]);
    try {
      const { data } = await api.post(`/admin/workspace-agents/${row.id}/run`, shops?.length ? { shops } : {});
      describeRunOutcome(row, data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to queue the run');
    }
  });

  const onRunClick = (row) => {
    // All-accounts rules pick store(s) first; single-account scopes run directly.
    if (row.targetShop === ALL) setRunDialog({ rule: row, shops: [] });
    else runRule(row);
  };

  const resync = (row) => withRowBusy(row, 'resync', async () => {
    try {
      const { data } = await api.post(`/admin/workspace-agents/${row.id}/resync`);
      toast.success(`"${data.row?.templateName || row.templateName}" re-synced (${data.updated} cop${data.updated === 1 ? 'y' : 'ies'}${data.skipped ? `, ${data.skipped} skipped` : ''})`);
      if (data.row) spliceRow(data.row);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to re-sync');
    }
  });

  const removeRule = (row) => {
    if (!window.confirm(`Remove "${row.templateName}" from ${row.targetAccountName} · ${row.targetWorkspaceName}? Past runs are kept, but the schedule stops and its hidden copies are deleted.`)) return;
    withRowBusy(row, 'remove', async () => {
      try {
        await api.delete(`/admin/workspace-agents/${row.id}`);
        toast.success('Assignment removed');
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        setTotal((prev) => Math.max(0, prev - 1));
        if (expandedId === row.id) setExpandedId('');
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Failed to remove the assignment');
      }
    });
  };

  const removeLegacy = (row) => {
    if (!window.confirm(`Remove the new-workspace tag from "${row.templateName}"? New workspaces will no longer run it. Recreate it as an All-accounts assignment to schedule it.`)) return;
    withRowBusy(row, 'remove', async () => {
      try {
        await api.post(`/admin/workflows/${row.templateId}/tags`, { tag: LEGACY_TAG, action: 'remove' });
        toast.success('Legacy tag removed');
        setLegacyRows((prev) => prev.filter((r) => r.id !== row.id));
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Failed to remove the tag');
      }
    });
  };

  const toggleEditor = (row) => {
    if (expandedId === row.id) {
      setExpandedId('');
      setDraft(null);
      return;
    }
    setExpandedId(row.id);
    setDraft({
      schedule: { type: 'off', time: '09:00', days: [], ...(row.schedule || {}) },
      enabled: row.enabled === true,
      dirty: false,
      saving: false,
    });
  };

  const saveSchedule = async (row) => {
    if (!draft || draft.saving) return;
    setDraft((d) => ({ ...d, saving: true }));
    try {
      const { data } = await api.put(`/admin/workspace-agents/${row.id}/schedule`, {
        schedule: draft.schedule,
        enabled: draft.enabled,
      });
      spliceRow(data);
      setExpandedId('');
      setDraft(null);
      toast.success(data.enabled ? 'Schedule saved' : 'Schedule saved (paused)');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save the schedule');
      setDraft((d) => (d ? { ...d, saving: false } : d));
    }
  };

  const pageFrom = total === 0 ? 0 : skip + 1;
  const pageTo = Math.min(skip + PAGE_SIZE, total);

  const renderRuleRow = (row) => {
    const busy = busyId === row.id;
    const missing = missingMessages(row);
    const badge = scheduleBadgeText(row.schedule);
    const expanded = expandedId === row.id;
    const isAll = row.targetWorkspaceId === ALL;
    return (
      <React.Fragment key={row.id}>
        <tr>
          <td style={s.td}>
            <div style={s.name}>{row.templateName}</div>
            <div style={s.meta}>{row.sourceAccountName} · {row.sourceWorkspaceName}</div>
            {(row.sourceDeleted || row.staleSource || row.targetWorkspaceGone || missing.length > 0) && (
              <div style={s.badges}>
                {missing.length > 0 && (
                  <span style={s.pill('#fef2f2', '#dc2626', '#fecaca')} title={missing.join(' ')}>Needs setup</span>
                )}
                {row.targetWorkspaceGone && (
                  <span style={s.pill('#fef2f2', '#dc2626', '#fecaca')} title="The destination workspace was deleted — remove this assignment.">Workspace deleted</span>
                )}
                {row.sourceDeleted && (
                  <span style={s.pill('#fef2f2', '#dc2626', '#fecaca')} title="The source workflow was deleted; the copies keep running as-is.">Source deleted</span>
                )}
                {!row.sourceDeleted && row.staleSource && (
                  <span style={s.pill('#fffbeb', '#b45309', '#fde68a')} title="The source workflow changed since the last re-sync.">Source updated</span>
                )}
              </div>
            )}
          </td>
          <td style={s.td}>
            <div>{row.targetAccountName}</div>
            <div style={s.meta}>{row.targetWorkspaceName}</div>
          </td>
          <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
            {row.enabled && badge
              ? <span style={s.pill('#ecfdf5', '#047857', '#a7f3d0')}>{badge}</span>
              : <span style={s.meta}>{badge ? `${badge} · paused` : 'Off'}</span>}
          </td>
          <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
            {isAll ? (
              row.lastStatus ? (
                <>
                  <span style={statusPill(row.lastStatus)} title={row.lastSummary || row.lastError || undefined}>{row.lastStatus}</span>
                  <div style={{ ...s.meta, marginTop: '3px' }}>{formatDateTime(row.lastRunAt)}</div>
                </>
              ) : <span style={s.meta}>Never</span>
            ) : (
              row.lastRun ? (
                <>
                  <span style={statusPill(row.lastRun.status)}>{row.lastRun.status || '—'}</span>
                  <div style={{ ...s.meta, marginTop: '3px' }}>{formatDateTime(row.lastRun.at)}</div>
                </>
              ) : <span style={s.meta}>Never</span>
            )}
          </td>
          <td style={{ ...s.td, ...s.meta, whiteSpace: 'nowrap' }}>
            {row.enabled ? formatDateTime(row.nextRunAt) : '—'}
          </td>
          <td style={{ ...s.td, textAlign: 'right' }}>
            <div style={s.actions}>
              <button
                type="button"
                style={s.actionBtn(busy || missing.length > 0 || row.targetWorkspaceGone)}
                disabled={busy || missing.length > 0 || row.targetWorkspaceGone}
                title={missing.length ? missing.join(' ') : row.targetShop === ALL ? 'Run for particular store(s)…' : 'Queue runs now'}
                onClick={() => onRunClick(row)}
              >
                {busy && busyAction === 'run' ? 'Queueing…' : row.targetShop === ALL ? 'Run…' : 'Run now'}
              </button>
              <button
                type="button"
                style={s.actionBtn(busy || row.sourceDeleted)}
                disabled={busy || row.sourceDeleted}
                title={row.sourceDeleted ? 'The source workflow no longer exists.' : 'Refresh the copies from the source workflow'}
                onClick={() => resync(row)}
              >
                {busy && busyAction === 'resync' ? 'Syncing…' : 'Re-sync'}
              </button>
              <button
                type="button"
                style={s.actionBtn(busy)}
                disabled={busy}
                title="Set this assignment's schedule"
                onClick={() => toggleEditor(row)}
              >
                {expanded ? 'Close' : 'Schedule'}
              </button>
              <button
                type="button"
                style={s.actionBtn(busy, true)}
                disabled={busy}
                title="Remove this assignment (past runs are kept)"
                onClick={() => removeRule(row)}
              >
                {busy && busyAction === 'remove' ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </td>
        </tr>
        {expanded && draft && (
          <tr>
            <td style={s.editorCell} colSpan={6}>
              <div style={s.editorRow}>
                <div style={s.editorLeft}>
                  <ScheduleFields
                    schedule={draft.schedule}
                    onChange={(next) => setDraft((d) => ({
                      ...d,
                      schedule: next,
                      // Turning the type off pauses the rule; the server stores
                      // enabled:false for type off regardless.
                      enabled: next.type === 'off' ? false : d.enabled,
                      dirty: true,
                    }))}
                  />
                  <ToggleSwitch
                    label="Enabled"
                    checked={draft.enabled}
                    onChange={() => {
                      if (draft.schedule.type === 'off') {
                        toast('Pick a repeat option first.', { icon: 'ℹ️' });
                        return;
                      }
                      if (!draft.enabled && missing.length) {
                        toast.error(missing.join(' '));
                        return;
                      }
                      setDraft((d) => ({ ...d, enabled: !d.enabled, dirty: true }));
                    }}
                  />
                </div>
                <button
                  type="button"
                  style={s.primaryBtn(!draft.dirty || draft.saving)}
                  disabled={!draft.dirty || draft.saving}
                  onClick={() => saveSchedule(row)}
                >
                  {draft.saving ? 'Saving…' : 'Save schedule'}
                </button>
              </div>
              {isAll && (
                <div style={{ ...s.hint, marginTop: '8px' }}>
                  Each occurrence runs every covered workspace — including workspaces and accounts created later.
                  Workspaces missing their ads setup are skipped and counted in the last-run summary.
                </div>
              )}
              {missing.length > 0 && (
                <ul style={s.missingList}>
                  {missing.map((m) => <li key={m}>{m}</li>)}
                </ul>
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderLegacyRow = (row) => {
    const busy = busyId === row.id;
    return (
      <tr key={`legacy-${row.id}`}>
        <td style={s.td}>
          <div style={s.name}>{row.templateName}</div>
          <div style={s.meta}>{row.sourceAccountName}</div>
          <div style={s.badges}>
            <span
              style={s.pill('#eef2ff', '#4338ca', '#c7d2fe')}
              title="Runs via the legacy new-workspace tag (bootstraps every new workspace; recurs only on the retired global Automated Context schedule). Recreate it as an All-accounts assignment to schedule it here."
            >
              Legacy tag
            </span>
          </div>
        </td>
        <td style={s.td}>
          <div>All accounts</div>
          <div style={s.meta}>All workspaces</div>
        </td>
        <td style={{ ...s.td, ...s.meta }}>—</td>
        <td style={{ ...s.td, ...s.meta }}>—</td>
        <td style={{ ...s.td, ...s.meta }}>—</td>
        <td style={{ ...s.td, textAlign: 'right' }}>
          <div style={s.actions}>
            <button
              type="button"
              style={s.actionBtn(busy, true)}
              disabled={busy}
              title="Remove the legacy new-workspace tag"
              onClick={() => removeLegacy(row)}
            >
              {busy && busyAction === 'remove' ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <p style={s.note}>
        Assign any workflow to any workspace of any account — or to <strong>All workspaces</strong> /
        <strong> All accounts</strong>, which automatically covers new accounts and workspaces as they are
        created. Each assignment keeps ONE hidden copy per covered workspace (bound to its ad accounts and
        website), runs on its own schedule or on demand, and stays invisible to the customer — only the
        delivered output shows. <strong>Re-sync</strong> pulls in later edits to the source workflow. Runs
        bill each workspace&apos;s own account.
      </p>

      <h3 style={s.sectionTitle}>Assign a workflow</h3>
      <div style={s.assignGrid}>
        <div style={s.assignCol}>
          <span style={s.assignLabel}>Source account</span>
          <SearchableSelect
            value={src.shop}
            options={storeOptions}
            onChange={(v) => setSrc({ shop: v, workspaceId: '', workflowId: '' })}
            placeholder="— Select account —"
          />
        </div>
        <div style={s.assignCol}>
          <span style={s.assignLabel}>Source workspace</span>
          <SearchableSelect
            value={src.workspaceId}
            options={srcWsOptions}
            onChange={(v) => setSrc((p) => ({ ...p, workspaceId: v, workflowId: '' }))}
            placeholder={src.shop ? '— All of the account —' : 'Pick an account first'}
            disabled={!src.shop}
          />
        </div>
        <div style={s.assignCol}>
          <span style={s.assignLabel}>Workflow</span>
          <SearchableSelect
            value={src.workflowId}
            options={srcFlowOptions}
            onChange={(v) => setSrc((p) => ({ ...p, workflowId: v }))}
            placeholder={src.shop ? '— Select workflow —' : 'Pick an account first'}
            disabled={!src.shop}
            emptyText="No workflows here"
          />
        </div>
        <div style={s.assignCol}>
          <span style={s.assignLabel}>Destination account</span>
          <SearchableSelect
            value={dst.shop}
            options={dstShopOptions}
            onChange={(v) => setDst({ shop: v, workspaceId: ALL })}
            placeholder="— Select destination —"
          />
        </div>
        <div style={s.assignCol}>
          <span style={s.assignLabel}>Destination workspace</span>
          <SearchableSelect
            value={dst.workspaceId}
            options={dstWsOptions}
            onChange={(v) => setDst((p) => ({ ...p, workspaceId: v }))}
            placeholder={dst.shop ? '— Select workspace —' : 'Pick a destination first'}
            disabled={!dst.shop || dst.shop === ALL}
          />
        </div>
      </div>
      <div style={s.assignFoot}>
        <span style={s.hint}>
          &ldquo;All workspaces&rdquo; auto-covers new workspaces of that account; &ldquo;All accounts&rdquo; covers
          new accounts too. Assigning doesn&apos;t run anything yet — use Run or a schedule below.
          {wsTruncated ? ' Only the first 500 workspaces are listed.' : ''}
        </span>
        <button
          type="button"
          style={s.primaryBtn(!assignReady || assigning)}
          disabled={!assignReady || assigning}
          onClick={assign}
        >
          {assigning ? 'Assigning…' : 'Assign'}
        </button>
      </div>

      <hr style={s.divider} />

      <h3 style={s.sectionTitle}>All assignments</h3>
      <div style={s.filters}>
        <input
          style={s.search}
          placeholder="Search by workflow name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={s.filterSelect}
          value={shopFilter}
          onChange={(e) => { setShopFilter(e.target.value); setWorkspaceFilter(''); }}
        >
          <option value="">All accounts</option>
          {stores.map((st) => <option key={st.shopKey} value={st.shopKey}>{st.name}</option>)}
        </select>
        <select style={s.filterSelect} value={workspaceFilter} onChange={(e) => setWorkspaceFilter(e.target.value)}>
          <option value="">All workspaces</option>
          {wsFilterOptions.map((w) => <option key={w.id} value={w.id}>{w.name || w.id}</option>)}
        </select>
      </div>

      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Workflow</th>
              <th style={s.th}>Destination</th>
              <th style={s.th}>Schedule</th>
              <th style={s.th}>Last run</th>
              <th style={s.th}>Next run</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={s.empty} colSpan={6}>Loading assignments…</td></tr>
            ) : (rows.length === 0 && legacyRows.length === 0) ? (
              <tr>
                <td style={s.empty} colSpan={6}>
                  No assignments yet — pick a workflow and a destination above.
                </td>
              </tr>
            ) : (
              <>
                {rows.map(renderRuleRow)}
                {legacyRows.map(renderLegacyRow)}
              </>
            )}
          </tbody>
        </table>
      </div>

      {runReport.length > 0 && (
        <ul style={s.reportList}>
          {runReport.map((f) => <li key={f}>{f}</li>)}
        </ul>
      )}

      <div style={s.pager}>
        <span>
          {total === 0 ? 'No assignments' : `Showing ${pageFrom}–${pageTo} of ${total}`}
          {legacyRows.length ? ` · ${legacyRows.length} legacy tag${legacyRows.length === 1 ? '' : 's'}` : ''}
        </span>
        <span style={{ display: 'flex', gap: '8px' }}>
          <button type="button" style={s.pageBtn(skip <= 0)} disabled={skip <= 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>← Previous</button>
          <button type="button" style={s.pageBtn(skip + PAGE_SIZE >= total)} disabled={skip + PAGE_SIZE >= total} onClick={() => setSkip(skip + PAGE_SIZE)}>Next →</button>
        </span>
      </div>

      {runDialog && (
        <div style={s.dialogOverlay} onClick={(e) => e.target === e.currentTarget && setRunDialog(null)}>
          <div style={s.dialog}>
            <span style={{ fontWeight: 700, fontSize: '14px' }}>Run &ldquo;{runDialog.rule.templateName}&rdquo;</span>
            <span style={s.hint}>
              Pick the store(s) to run it for, or leave empty to run every covered store
              (at most {RUN_MAX_WORKSPACES} workspaces per go).
            </span>
            <SearchableMultiSelect
              value={runDialog.shops}
              options={storeOptions}
              onChange={(next) => setRunDialog((d) => ({ ...d, shops: next }))}
              placeholder="— All covered stores —"
              summarize={(n) => `${n} stores selected`}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" style={s.actionBtn(false)} onClick={() => setRunDialog(null)}>Cancel</button>
              <button
                type="button"
                style={s.primaryBtn(busyId === runDialog.rule.id)}
                disabled={busyId === runDialog.rule.id}
                onClick={() => { const { rule, shops } = runDialog; setRunDialog(null); runRule(rule, shops); }}
              >
                Run
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
